import React, {memo, useCallback, useEffect, useRef, useState} from "react";
import {Header} from "./Header";
import {Outlet} from "react-router-dom";
import FullScreenMenu from "./FullScreenMenu";
import Footer from "./Footer";
import menuData from "../../config/menuData";
import {ContactModal} from "./ContactModal";
import ModalContext from "../context/ModalContext";
import {CeoModal} from "./CeoModal";

const MemoizedHeader = memo(Header);
const MemoizedFullScreenMenu = memo(FullScreenMenu);
const MemoizedFooter = memo(Footer);
const MemoizedContactModal = memo(ContactModal);

const Layout: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [isDesignerModalOpen, setIsDesignerModalOpen] = useState(false);
    const [selectedDesigner, setSelectedDesigner] = useState(null);

    const handleScroll = useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            const container = scrollContainerRef.current;
            if (container) {
                const scrollTop = container.scrollTop;
                setIsScrolled(scrollTop > 50);
            }
        }, 10);
    }, []);

    const handleMenuOpen = useCallback(() => setIsMenuOpen(true), []);
    const handleMenuClose = useCallback(() => setIsMenuOpen(false), []);
    const handleEnquiryModalOpen = useCallback(() => setIsEnquiryModalOpen(true), []);
    const handleEnquiryModalClose = useCallback(() => setIsEnquiryModalOpen(false), []);

    const openDesignerModal = useCallback((designer: any) => {
        setSelectedDesigner(designer);
        setIsDesignerModalOpen(true);
    }, []);

    const closeDesignerModal = useCallback(() => {
        setIsDesignerModalOpen(false);
        setSelectedDesigner(null);
    }, []);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener('scroll', handleScroll, {passive: true});

        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.style.overflowY = isMenuOpen || isDesignerModalOpen ? 'hidden' : 'auto';
    }, [isMenuOpen, isDesignerModalOpen]);

    // Keyboard Navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (isEnquiryModalOpen) {
                    handleEnquiryModalClose();
                } else if (isMenuOpen) {
                    handleMenuClose();
                } else if (isDesignerModalOpen) {
                    closeDesignerModal();
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isEnquiryModalOpen, isMenuOpen, isDesignerModalOpen, handleEnquiryModalClose, handleMenuClose, closeDesignerModal]);

    return (
        <ModalContext.Provider value={{openDesignerModal}}>
            <div className="absolute overflow-hidden h-screen w-full">
                <div
                    ref={scrollContainerRef}
                    className="overflow-y-scroll main-container overflow-x-hidden h-screen"
                    style={{
                        willChange: 'scroll-position',
                    }}
                >
                    {/* Header */}
                    <MemoizedHeader
                        isScrolled={isScrolled}
                        menuData={menuData}
                        onMenuOpen={handleMenuOpen}
                    />

                    {/* Main Content Area */}
                    <main>
                        <Outlet/>
                    </main>

                    {/* Footer */}
                    <MemoizedFooter onMenuOpen={handleMenuOpen}/>
                </div>

                {/* Full Screen Menu */}
                <MemoizedFullScreenMenu
                    isMenuOpen={isMenuOpen}
                    onMenuClose={handleMenuClose}
                    onEnquireClick={handleEnquiryModalOpen}
                    menuData={menuData}
                />

                {/* Contact Modal */}
                <MemoizedContactModal
                    isOpen={isEnquiryModalOpen}
                    onClose={handleEnquiryModalClose}
                />

                {/* Designer Modal */}
                <CeoModal
                    isOpen={isDesignerModalOpen}
                    onClose={closeDesignerModal}
                    designer={selectedDesigner}
                />
            </div>
        </ModalContext.Provider>
    )
}

export default memo(Layout);